import {
  type AfterViewInit,
  type OnDestroy,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  model,
  signal,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router, type Params } from '@angular/router';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { Sort } from '@angular/material/sort';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import {
  GeographicDataService,
  CityOption,
  StateOption,
} from '../../../shared/services/geographic-data.service';
import { OrganizationService } from '../../../shared/services/organization.service';
import { SmartOrpcClientService } from '../../../shared/services/smart-orpc-client.service';
import {
  MonitoringResultsFilters,
  MonitoringResultsResponse,
} from '../../../shared/types/monitoring-results.types';
import { TotalizerComponent } from './totalizer/totalizer.component';
import { TableMonitoringSafraResultsComponent } from './table-monitoring-safra-results/table-monitoring-safra-results.component';
import { ResultsMapComponent } from './results-map/results-map.component';
import { UserService } from '../../../shared/services/user.service';
import { MonitoringResultsStateService } from '../../../shared/services/monitoring-results-state.service';
import { contractOutput } from '#backend/contract';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import type { HarvestMonitoringAtlas } from '#backend/entity/loaded/harvest-monitoring/HarvestMonitoringAtlas';

type Organization = contractOutput['organization']['list']['items'][number];

@Component({
  selector: 'app-monitoring-safra-results',
  imports: [
    MatButtonToggleModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    MatDatepickerModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatMenuModule,
    MatSelectModule,
    MatButtonModule,
    MatRadioModule,
    TotalizerComponent,
    TableMonitoringSafraResultsComponent,
    MatPaginatorModule,
    ResultsMapComponent,
    MatProgressBarModule,
    MatProgressSpinner,
  ],
  templateUrl: './monitoring-safra-results.component.html',
  styleUrl: './monitoring-safra-results.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MonitoringSafraResultsComponent
  implements AfterViewInit, OnDestroy
{
  hideSingleSelectionIndicator = signal(false);
  readonly checked = model(false);

  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);
  private readonly geoService = inject(GeographicDataService);
  private readonly organizationService = inject(OrganizationService);
  private readonly smartClient = inject(SmartOrpcClientService);
  protected readonly userService = inject(UserService);
  private readonly stateService = inject(MonitoringResultsStateService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  @ViewChild('safraMenuTrigger') safraMenuTrigger!: MatMenuTrigger;

  // Organizations for autocomplete
  protected organizations: Organization[] = [];
  protected filteredOrganizations: Organization[] = [];
  private abortController?: AbortController;

  filtrosForm: FormGroup = this.fb.group({
    uf: [''],
    municipio: [''],
  });

  businessFiltersForm = this.fb.group({
    client: [null as Organization | string | null], // Organization object or special string values
    areaId: [''],
    producer: [''],
    farmName: [''],
    car: [''],
    cpfCnpj: [''],
    contract: [''],
    insurancePolicy: [''],
  });

  avisos = [
    {
      id: 'aptidao',
      label: 'Presença de Aptidão de Colheita',
      control: new FormControl(false, { nonNullable: true }),
    },
    {
      id: 'senescencia',
      label: 'Presença de Senescência',
      control: new FormControl(false, { nonNullable: true }),
    },
  ] as const;

  alertas = [
    {
      id: 'naoPlantado',
      label: 'Não Plantado',
      control: new FormControl(false, { nonNullable: true }),
    },
    {
      id: 'culturaDivergente',
      label: 'Presença de Cultura Divergente',
      control: new FormControl(false, { nonNullable: true }),
    },
  ] as const;

  /**
   * Filtro por status de zoneamento ZARC.
   * Cada opção corresponde a um valor do campo `zarc_status` na view:
   *   'found'     → "Com Zoneamento"          (risco 20|30|40 calculado)
   *   'not_found' → "Fora do Zoneamento"       (calculado, sem correspondência)
   *   'pending'   → "Sem Informação de ZARC" (ainda não processado)
   */
  zarcStatusFilters = [
    {
      id: 'withZoning' as const,
      value: 'found' as const,
      label: 'Com Zoneamento',
      control: new FormControl(false, { nonNullable: true }),
    },
    {
      id: 'withoutZoning' as const,
      value: 'not_found' as const,
      label: 'Fora do Zoneamento',
      control: new FormControl(false, { nonNullable: true }),
    },
  ] as const;

  cultures: CultureOption[] = [];
  safras: string[] = [];

  selectedCultureId: number | null = null;
  culturaSelecionada = '';
  safra: string | null = null;

  ufOptions: StateOption[] = [];
  municipios: CityOption[] = [];
  filteredMunicipios = signal<CityOption[]>([]);

  private stateNameToAbbreviationMap = new Map<string, string>();
  readonly resultResponse = signal<MonitoringResultsResponse | null>(null);
  readonly isLoading = signal(false);
  readonly isFiltersLoading = signal(false);

  pageIndex = 0;
  pageSize = 10;

  protected currentSort: Sort = { active: 'municipio', direction: 'asc' };

  private isRestoringState = false; // Flag para evitar loops

  get canSearch(): boolean {
    return this.selectedCultureId !== null && this.safra !== null;
  }

  ngOnDestroy() {
    this.abortController?.abort();
  }

  async ngAfterViewInit(): Promise<void> {
    this.filtrosForm.disable();
    this.businessFiltersForm.disable();
    await Promise.all([
      this.loadGeographicData(),
      this.loadFilterOptions(),
      this.loadOrganizations(),
      this.loadCropYears(),
    ]).finally(() => {
      this.filtrosForm.enable();
      this.businessFiltersForm.enable();
    });

    // Configura filtro de organizações
    const clientControl = this.businessFiltersForm.get('client');
    if (clientControl) {
      clientControl.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((value) => {
          if (typeof value === 'string') {
            this.filterOrganizations(value);
          }
        });
    }

    // Restaura estado APENAS após carregar os dados geográficos
    const params = this.route.snapshot.queryParams;
    if (Object.keys(params).length > 0) {
      this.isRestoringState = true;
      this.restoreStateFromQueryParams(params);
      this.isRestoringState = false;
    }

    // Monitora mudanças nos filtros obrigatórios e faz busca automática
    this.setupAutoSearch();
  }

  private setupAutoSearch(): void {
    // Observa mudanças no campo UF
    const ufControl = this.filtrosForm.get('uf');
    if (ufControl) {
      ufControl.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          if (this.canSearch && !this.isRestoringState) {
            this.pageIndex = 0;
            void this.loadResults();
          }
        });
    }

    // Configura filtragem de municípios ao digitar (sem fazer busca)
    const municipioControl = this.filtrosForm.get('municipio');
    if (municipioControl) {
      municipioControl.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((value) => {
          if (typeof value === 'string') {
            // Se o usuário está digitando (string), apenas filtra as opções
            this.filteredMunicipios.set(this.filterMunicipios(value));
          }
        });
    }
  }

  /**
   * Callback chamado quando uma opção é selecionada no autocomplete de município
   */
  onMunicipioSelected(): void {
    if (this.canSearch && !this.isRestoringState) {
      this.pageIndex = 0;
      void this.loadResults();
    }
  }

  toggleSingleSelectionIndicator() {
    this.hideSingleSelectionIndicator.update((value) => !value);
  }

  async onUfChange(uf: string) {
    // Converte nome completo para abreviação para filtrar cidades
    const stateAbbreviation = this.stateNameToAbbreviationMap.get(uf) || uf;
    this.filterCitiesByStateAbbreviation(stateAbbreviation);
    this.filtrosForm.get('municipio')?.setValue('', { emitEvent: false });
  }

  /**
   * Filtra cidades por abreviação do estado
   */
  private filterCitiesByStateAbbreviation(stateAbbreviation: string): void {
    if (!stateAbbreviation) {
      // Mostra todas as cidades se nenhum estado selecionado
      this.filteredMunicipios.set(this.municipios);
    } else {
      // Busca cidades através do serviço usando a abreviação
      this.geoService.filterCitiesByState(stateAbbreviation);
      // O serviço atualizará via subscription
    }
  }

  /**
   * Filtra cidades por texto digitado
   */
  filterMunicipios(value: string): CityOption[] {
    if (!value) {
      return this.municipios;
    }
    const filterValue = value.toLowerCase();
    return this.municipios.filter((municipio) =>
      municipio.label.toLowerCase().includes(filterValue),
    );
  }

  /**
   * Exibe o label da cidade selecionada no autocomplete
   */
  displayMunicipio = (value: string): string => {
    if (!value) return '';
    const municipio = this.municipios.find((m) => m.value === value);
    return municipio ? municipio.label : '';
  };

  /**
   * Carrega as organizações para o autocomplete
   */
  private async loadOrganizations(): Promise<void> {
    try {
      if (!this.userService.isGeoambiente) return;
      this.organizations = await this.organizationService.getOrganizations(
        false,
        true,
      );
      this.filteredOrganizations = this.organizations;
    } catch (error) {
      console.error('Erro ao carregar organizações:', error);
    }
  }

  /**
   * Filtra organizações por texto digitado
   */
  private filterOrganizations(searchTerm: string): void {
    if (!searchTerm) {
      this.filteredOrganizations = this.organizations;
      return;
    }
    const term = searchTerm.toLowerCase();
    this.filteredOrganizations = this.organizations.filter((org) =>
      org.name.toLowerCase().includes(term),
    );
  }

  /**
   * Exibe o nome da organização no autocomplete
   */
  protected displayOrganizationName = (
    org: Organization | string | null,
  ): string => {
    if (!org) return '';
    if (typeof org === 'string') {
      // Trata valores especiais
      if (org === '__ALL__') return 'Todos';
      if (org === 'Geoambiente') return 'Geoambiente';
      return org;
    }
    return org?.name || '';
  };

  /**
   * Limpa apenas o filtro da safra
   */
  clearSafraFilter() {
    this.safra = null;
    this.culturaSelecionada = '';

    // Limpa os resultados quando limpar a safra
    this.resultResponse.set(null);
    this.stateService.clearSelection();
  }

  /**
   * Limpa apenas os filtros de negócio
   */
  clearBusinessFilters() {
    this.businessFiltersForm.reset({}, { emitEvent: false });
  }

  /**
   * Limpa apenas os filtros de alertas e avisos
   */
  clearAlertsFilters() {
    this.avisos.forEach(({ control }) =>
      control.setValue(false, { emitEvent: false }),
    );
    this.alertas.forEach(({ control }) =>
      control.setValue(false, { emitEvent: false }),
    );
  }

  /**
   * Limpa apenas os filtros de status de ZARC
   */
  clearZarcFilters() {
    this.zarcStatusFilters.forEach(({ control }) =>
      control.setValue(false, { emitEvent: false }),
    );
  }

  /**
   * Limpa TODOS os filtros (mantido para o botão "Limpar todos os filtros")
   */
  clearFilters() {
    // Limpa os campos obrigatórios primeiro para desabilitar canSearch
    this.safra = null;
    this.selectedCultureId = null;
    this.culturaSelecionada = '';

    // Reset dos formulários com emitEvent: false para não disparar busca automática
    this.businessFiltersForm.reset({}, { emitEvent: false });
    this.filtrosForm.reset({ uf: '', municipio: '' }, { emitEvent: false });

    // Reset dos checkboxes sem disparar valueChanges
    this.avisos.forEach(({ control }) =>
      control.setValue(false, { emitEvent: false }),
    );
    this.alertas.forEach(({ control }) =>
      control.setValue(false, { emitEvent: false }),
    );
    this.zarcStatusFilters.forEach(({ control }) =>
      control.setValue(false, { emitEvent: false }),
    );

    // Limpa o filtro de cidades
    this.filterCitiesByStateAbbreviation('');

    // Limpa os resultados quando limpar os filtros
    // Limpa a seleção de linha destacada
    this.stateService.clearSelection();

    // Limpa os queryParams
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      replaceUrl: true,
    });
    this.resultResponse.set(null);
  }
  /**
   * Aplica o filtro da safra e fecha o menu
   */
  applySafraFilter() {
    if (this.canSearch) {
      this.pageIndex = 0;
      this.stateService.clearSelection();
      void this.loadResults();
    }

    // Fecha o menu da safra programaticamente
    if (this.safraMenuTrigger) {
      this.safraMenuTrigger.closeMenu();
    }
  } // Fecha o menu da safra programaticamente
  // Não precisa fazer nada específico, o menu fecha automaticamente quando clica em um botão

  applyFilters() {
    if (!this.canSearch) {
      return;
    }
    this.pageIndex = 0;
    this.stateService.clearSelection(); // Limpa destaque ao aplicar novos filtros
    void this.loadResults();
  }

  async onCulturaChange(culturaId: number | null) {
    this.selectedCultureId = culturaId;
    this.updateSelectedCultureDescription();
    if (this.canSearch) {
      this.pageIndex = 0;
      await this.loadResults();
    }
  }

  selecionarCultura(culturaId: number | null) {
    void this.onCulturaChange(culturaId);
  }

  async onPageChange(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    await this.loadResults();
  }

  onSortChange(event: Sort) {
    this.currentSort = event;
    this.pageIndex = 0; // Reset para primeira página ao ordenar
    void this.loadResults();
  }

  private async loadGeographicData(): Promise<void> {
    // Para este componente específico, carregamos dados mistos:
    // Estados com valor completo e label abreviado
    // Cidades com abreviações do estado
    await Promise.all([
      this.loadStatesForBusinessFilters(),
      this.geoService.loadCitiesWithAbbreviations(),
    ]);

    // Observa mudanças nas cidades do serviço
    this.geoService
      .getCitiesOptions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((cities) => {
        this.municipios = cities;
        this.filteredMunicipios.set(cities);
      });
  }

  /**
   * Carrega estados específicamente para filtros de negócio
   * Valor = nome completo (para backend), Label = abreviação (para UI)
   */
  private async loadStatesForBusinessFilters(): Promise<void> {
    try {
      const statesData =
        await this.smartClient.client.ibgeMunicipios.statesAbbreviations();
      const stateOptions: StateOption[] = statesData.map((item) => ({
        value: item.state, // Nome completo para backend (ex: "São Paulo")
        label: item.stateuf, // Abreviação para display (ex: "SP")
      }));

      // Constrói mapeamento para conversão
      this.stateNameToAbbreviationMap.clear();
      statesData.forEach((item) => {
        this.stateNameToAbbreviationMap.set(item.state, item.stateuf);
      });

      // Aplica os estados diretamente sem usar o serviço
      this.ufOptions = stateOptions;
    } catch (error) {
      console.error('Error loading states for business filters:', error);
      this.ufOptions = [];
    }
  }

  private async loadFilterOptions(): Promise<void> {
    this.isFiltersLoading.set(true);
    try {
      const culturesResult =
        await this.smartClient.safeClient.monitoring.cultures.list({});

      if (culturesResult.data) {
        this.cultures = culturesResult.data;
        // Não seleciona cultura padrão - usuário deve selecionar manualmente
      }
    } finally {
      this.isFiltersLoading.set(false);
    }
  }

  /**
   * Carrega as safras disponíveis a partir do banco de dados.
   * - Clientes: apenas as safras das suas próprias solicitações.
   * - Geoambiente: todas as safras de todos os clientes.
   */
  private async loadCropYears(): Promise<void> {
    const result =
      await this.smartClient.safeClient.monitoring.availableCropYears();
    if (result.error) {
      return;
    }
    this.safras = result.data ?? [];
  }

  // Mapeamento de nomes de colunas do frontend para nomes de campos do backend
  private readonly columnToFieldMap: Record<
    string,
    keyof HarvestMonitoringAtlas
  > = {
    area_monitorada: 'monitoredAreaHa',
    area_cultura: 'plantedCultureAreaHa',
    em_desenvolvimento: 'developingAreaHa',
    em_senescencia: 'senescenceAreaHa',
    apto_para_colheita: 'readyForHarvestAreaHa',
    area_colhida: 'harvestedAreaHa',
    cultura_divergente: 'divergentCultureAreaHa',
    nao_plantado: 'notPlantedAreaHa',
    aguardando_identificacao: 'awaitingIdentificationAreaHa',
    possivel_cultura: 'possiblyTargetCultureAreaHa',
    zarc: 'zarcRisk',
    idArea: 'areaId',
    nome_fazenda: 'farmName',
    produtor: 'producer',
    cpf_cnpj: 'cpfCnpj',
    uf: 'state',
    municipio: 'city',
    car: 'car',
    contrato: 'contract',
    apolice: 'insurancePolicy',
    cliente: 'organizationName',
  };

  private async loadResults(): Promise<void> {
    this.abortController?.abort();
    this.abortController = new AbortController();
    if (!this.canSearch) {
      return;
    }

    this.isLoading.set(true);
    try {
      const filters = this.buildFilters();
      const cursor = this.pageIndex * this.pageSize;

      // Converte ordenação do Material para formato do backend
      let orderBy: Record<string, 'ASC' | 'DESC'> = { city: 'ASC' };

      if (this.currentSort.active && this.currentSort.direction) {
        const backendField =
          this.columnToFieldMap[this.currentSort.active] ||
          this.currentSort.active;
        orderBy = {
          [backendField]: this.currentSort.direction.toUpperCase() as
            'ASC' | 'DESC',
        };
      }

      const { data, error } =
        await this.smartClient.safeClient.monitoring.results(
          {
            filters,
            pageParams: {
              cursor,
              limit: this.pageSize,
            },
            orderBy,
          },
          { signal: this.abortController.signal },
        );

      if (error) {
        return;
      }

      if (!data) {
        this.resultResponse.set(null);
        return;
      }

      this.resultResponse.set(data);
      this.updateSelectedCultureDescription();

      // Atualiza os queryParams na URL
      this.updateQueryParams();
    } finally {
      this.isLoading.set(false);
    }
  }

  private buildFilters(): MonitoringResultsFilters['filters'] {
    const formValue = this.filtrosForm.getRawValue();
    const businessValue = this.businessFiltersForm.getRawValue();

    const warningSenescence =
      this.avisos.find((a) => a.id === 'senescencia')?.control.value ?? false;
    const warningHarvest =
      this.avisos.find((a) => a.id === 'aptidao')?.control.value ?? false;
    const alertDivergentCulture =
      this.alertas.find((a) => a.id === 'culturaDivergente')?.control.value ??
      false;
    const alertNotPlanted =
      this.alertas.find((a) => a.id === 'naoPlantado')?.control.value ?? false;

    // Extrai campos específicos de negócios
    const businessFilters: {
      organizationId?: number | null;
      areaId?: string;
      producer?: string;
      farmName?: string;
      car?: string;
      cpfCnpj?: string;
      contract?: string;
      insurancePolicy?: string;
    } = {};

    // Extrai organização selecionada e converte para organizationId
    if (businessValue.client && typeof businessValue.client === 'object') {
      // É um objeto Organization - envia o ID
      businessFilters.organizationId = businessValue.client.id;
    } else if (
      typeof businessValue.client === 'string' &&
      businessValue.client.trim()
    ) {
      // Trata valores especiais
      const clientValue = businessValue.client.trim();
      if (clientValue === '__ALL__') {
        // Não adiciona filtro de organização para buscar todos
        // businessFilters.organizationId permanece undefined
      } else if (clientValue === 'Geoambiente') {
        // Define como null para buscar usuários sem organização
        businessFilters.organizationId = null;
      }
      // Outras strings digitadas serão ignoradas - apenas objetos Organization são válidos
    }

    if (businessValue.areaId?.trim()) {
      businessFilters.areaId = businessValue.areaId.trim();
    }
    if (businessValue.producer?.trim()) {
      businessFilters.producer = businessValue.producer.trim();
    }
    if (businessValue.farmName?.trim()) {
      businessFilters.farmName = businessValue.farmName.trim();
    }
    if (businessValue.car?.trim()) {
      businessFilters.car = businessValue.car.trim();
    }
    if (businessValue.cpfCnpj?.trim()) {
      businessFilters.cpfCnpj = businessValue.cpfCnpj.trim();
    }
    if (businessValue.contract?.trim()) {
      businessFilters.contract = businessValue.contract.trim();
    }
    if (businessValue.insurancePolicy?.trim()) {
      businessFilters.insurancePolicy = businessValue.insurancePolicy.trim();
    }

    // Filtro de status ZARC (OR entre opções selecionadas)
    const selectedZarcStatuses = this.zarcStatusFilters
      .filter(({ control }) => control.value)
      .map(({ value }) => value) as ('found' | 'not_found' | 'pending')[];

    return {
      cropYear: this.safra ?? undefined,
      state: formValue.uf || undefined,
      city: formValue.municipio || undefined,
      culture: this.selectedCultureId ?? undefined,
      warningSenescence: warningSenescence ? true : undefined,
      warningHarvest: warningHarvest ? true : undefined,
      // Individual alert filters
      alertDivergentCulture: alertDivergentCulture ? true : undefined,
      alertNotPlanted: alertNotPlanted ? true : undefined,
      // Filtro de status ZARC (OR entre opções selecionadas)
      zarcStatus:
        selectedZarcStatuses.length > 0 ? selectedZarcStatuses : undefined,
      ...businessFilters,
    };
  }

  private updateSelectedCultureDescription() {
    if (!this.selectedCultureId) {
      this.culturaSelecionada = 'Todas as culturas';
      return;
    }

    const culture = this.cultures.find(
      (item) => item.id === this.selectedCultureId,
    );
    this.culturaSelecionada = culture?.description ?? 'Todas as culturas';
  }

  /**
   * Restaura o estado dos filtros e paginação a partir dos queryParams
   */
  private restoreStateFromQueryParams(params: Params): void {
    let hasFilters = false;

    // Restaura filtros obrigatórios
    if (params['cropYear']) {
      this.safra = params['cropYear'];
      hasFilters = true;
    }

    if (params['culture']) {
      this.selectedCultureId = parseInt(params['culture'], 10);
      this.updateSelectedCultureDescription();
      hasFilters = true;
    }

    // Restaura paginação
    if (params['page']) {
      this.pageIndex = parseInt(params['page'], 10);
    }

    if (params['pageSize']) {
      this.pageSize = parseInt(params['pageSize'], 10);
    }

    // Restaura ordenação
    if (params['sortBy'] && params['sortDirection']) {
      this.currentSort = {
        active: params['sortBy'],
        direction: params['sortDirection'] as 'asc' | 'desc' | '',
      };
    }

    // Restaura filtros geográficos
    if (params['state']) {
      this.filtrosForm.patchValue(
        { uf: params['state'] },
        { emitEvent: false },
      );
      // Converte para abreviação e filtra cidades
      const stateAbbreviation =
        this.stateNameToAbbreviationMap.get(params['state']) || params['state'];
      this.filterCitiesByStateAbbreviation(stateAbbreviation);
    }

    if (params['city']) {
      this.filtrosForm.patchValue(
        { municipio: params['city'] },
        { emitEvent: false },
      );
    }

    // Restaura filtros de negócio
    const businessFilters: Partial<typeof this.businessFiltersForm.value> = {};

    // Restaura cliente (organização) convertendo organizationId para objeto Organization
    if (params['organizationId']) {
      const orgId = parseInt(params['organizationId'], 10);
      if (orgId) {
        const organization = this.organizations.find((org) => org.id === orgId);
        if (organization) {
          businessFilters.client = organization;
        }
      } else if (params['organizationId'] === 'null') {
        // organizationId === null significa "Geoambiente"
        businessFilters.client = 'Geoambiente';
      }
    } else if (
      params['organizationId'] === 'undefined' ||
      params['organizationId'] === '__ALL__'
    ) {
      // undefined ou __ALL__ significa "Todos"
      businessFilters.client = '__ALL__';
    }

    if (params['areaId']) businessFilters.areaId = params['areaId'];
    if (params['producer']) businessFilters.producer = params['producer'];
    if (params['farmName']) businessFilters.farmName = params['farmName'];
    if (params['car']) businessFilters.car = params['car'];
    if (params['cpfCnpj']) businessFilters.cpfCnpj = params['cpfCnpj'];
    if (params['contract']) businessFilters.contract = params['contract'];
    if (params['insurancePolicy'])
      businessFilters.insurancePolicy = params['insurancePolicy'];

    if (Object.keys(businessFilters).length > 0) {
      this.businessFiltersForm.patchValue(businessFilters, {
        emitEvent: false,
      });
    }

    if (params['warningSenescence'] === 'true') {
      const senescenciaAviso = this.avisos.find((a) => a.id === 'senescencia');
      if (senescenciaAviso) {
        senescenciaAviso.control.setValue(true, { emitEvent: false });
      }
    }
    if (params['warningHarvest'] === 'true') {
      const aptidaoAviso = this.avisos.find((a) => a.id === 'aptidao');
      if (aptidaoAviso) {
        aptidaoAviso.control.setValue(true, { emitEvent: false });
      }
    }
    if (params['alertDivergentCulture'] === 'true') {
      const divergenteAlerta = this.alertas.find(
        (a) => a.id === 'culturaDivergente',
      );
      if (divergenteAlerta) {
        divergenteAlerta.control.setValue(true, { emitEvent: false });
      }
    }
    if (params['alertNotPlanted'] === 'true') {
      const naoPlantadoAlerta = this.alertas.find(
        (a) => a.id === 'naoPlantado',
      );
      if (naoPlantadoAlerta) {
        naoPlantadoAlerta.control.setValue(true, { emitEvent: false });
      }
    }

    // Restaura filtros de ZARC status
    const zarcStatusParam = params['zarcStatus'];
    if (zarcStatusParam) {
      const statuses = (
        Array.isArray(zarcStatusParam) ? zarcStatusParam : [zarcStatusParam]
      ) as string[];
      this.zarcStatusFilters.forEach((f) => {
        if (statuses.includes(f.value)) {
          f.control.setValue(true, { emitEvent: false });
        }
      });
    }

    // Se tem filtros, carrega os resultados
    if (hasFilters) {
      void this.loadResults();
    }
  }

  /**
   * Retorna os filtros atuais em formato de objeto
   */
  getCurrentFilters() {
    const filters = this.buildFilters();
    return {
      ...filters,
      cropYear: this.safra,
      culture: this.selectedCultureId,
    };
  }

  /**
   * Atualiza os queryParams na URL sem recarregar o componente
   */
  private updateQueryParams(): void {
    // Não atualiza se estiver restaurando o estado (evita loop)
    if (this.isRestoringState) {
      return;
    }

    const filters = this.getCurrentFilters();
    const queryParams = {
      ...filters,
      page: this.pageIndex,
      pageSize: this.pageSize,
      sortBy: this.currentSort.active || undefined,
      sortDirection: this.currentSort.direction || undefined,
    };

    // Remove valores undefined/null
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value == null) {
        delete queryParams[key as keyof typeof queryParams];
      }
    });

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}

interface CultureOption {
  id: number;
  description: string;
  enable: boolean;
}
